/**
 * rebateTest.ts — REBATE-TEST-01 (Seth 2026-09-25: "If a rebate is added to a
 * job and it's a cooling rebate then the rebate form should be added for the
 * installers to fill out").
 *
 * The job packet's "Rebate Test Form" tab, field for field and in the sheet's
 * numbering, filled on the installer's phone. It shows only when
 * job_rebate_test_status() says the job needs it: an AC / heat pump / mini-split
 * going in AND a rebate from a utility that supplies electric (on the accepted
 * proposal or the job's post-install rebate) — or the office forced it on
 * (pegasus_job.rebate_test). Pegasus fills what it knows (the customer, the
 * models, the install date, today, the tech, the tech's ID from their last form)
 * and does the arithmetic (splits, subcool, differences); the TXV / piston
 * readings are shared with Log the Charge.
 *
 * One fix against the paper: line 9 reads "DIFFERENCE (6)-(7)" — line 6 is the
 * supply wet bulb. Lines 18 and 17A are target minus actual, so line 9 here is
 * target split (8) minus actual split (7).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type RebateData = Record<string, string | number | null | undefined>;

export interface RebateField {
  key: string;
  n: string;          // the sheet's line number ("1", "13A"); "" for the header
  label: string;
  unit?: string;
  kind: "number" | "text" | "date" | "choice" | "computed";
  options?: { v: string; label: string }[];
  required?: boolean;
}

export const FAN_SPEEDS = [
  { v: "L", label: "L" }, { v: "M", label: "M" }, { v: "MH", label: "MH" }, { v: "H", label: "H" }, { v: "ECM", label: "ECM" },
];
export const DUCT_METHODS = [
  { v: "mastic", label: "UL-181B-M Approved and Labeled Mastic", n: "19" },
  { v: "foil_tape", label: "UL-181B-FX Foil Faced Butyl Backed Tape", n: "20" },
  { v: "mastic_mesh", label: "Mastic with Fiberglass Mesh Tape (9×9 Mesh Weave)", n: "21" },
];

export const HEADER_FIELDS: RebateField[] = [
  { key: "test_date", n: "", label: "Test Date", kind: "date", required: true },
  { key: "tech_name", n: "", label: "Tech", kind: "text", required: true },
  { key: "tech_id", n: "", label: "Tech ID", kind: "text" },
  { key: "outside_temp", n: "", label: "Outside Temp", unit: "°F", kind: "number", required: true },
];
export const SIZING_FIELDS: RebateField[] = [
  { key: "amps_compressor", n: "1", label: "Amps — Compressor", unit: "A", kind: "number", required: true },
  { key: "amps_furnace", n: "2", label: "Amps — Furnace", unit: "A", kind: "number", required: true },
];
export const AIRFLOW_FIELDS: RebateField[] = [
  { key: "return_db", n: "3", label: "Return Dry Bulb Temp", unit: "°F", kind: "number", required: true },
  { key: "return_wb", n: "4", label: "Return Wet Bulb Temp", unit: "°F", kind: "number", required: true },
  { key: "supply_db", n: "5", label: "Supply Dry Bulb Temp", unit: "°F", kind: "number", required: true },
  { key: "supply_wb", n: "6", label: "Supply Wet Bulb Temp", unit: "°F", kind: "number", required: true },
  { key: "actual_split", n: "7", label: "Actual Temp Split (3) − (5)", unit: "°F", kind: "computed" },
  { key: "target_split", n: "8", label: "Target Temp Split", unit: "°F", kind: "number", required: true },
  { key: "split_diff", n: "9", label: "Difference (8) − (7)", unit: "°F", kind: "computed" },
  { key: "esp", n: "10", label: "Total External Static Pressure", unit: "IWC", kind: "number", required: true },
  { key: "fan_speed", n: "11", label: "CFM Fan Speed", kind: "choice", options: FAN_SPEEDS, required: true },
  { key: "cfm", n: "12", label: "CFM", kind: "number", required: true },
];
export const TXV_FIELDS: RebateField[] = [
  { key: "liquid_psi", n: "13", label: "Liquid Line Pressure", unit: "psig", kind: "number", required: true },
  { key: "liquid_sat", n: "14", label: "Liquid Saturation Temp", unit: "°F", kind: "number", required: true },
  { key: "liquid_temp", n: "15", label: "Liquid Line Temp", unit: "°F", kind: "number", required: true },
  { key: "subcool_actual", n: "16", label: "Actual Subcool (14) − (15)", unit: "°F", kind: "computed" },
  { key: "subcool_target", n: "17", label: "Target Subcool", unit: "°F", kind: "number", required: true },
  { key: "subcool_diff", n: "18", label: "Difference (17) − (16)", unit: "°F", kind: "computed" },
];
export const PISTON_FIELDS: RebateField[] = [
  { key: "suction_psi", n: "13A", label: "Suction Line Pressure", unit: "psig", kind: "number", required: true },
  { key: "suction_temp", n: "14A", label: "Suction Line Temperature", unit: "°F", kind: "number", required: true },
  { key: "superheat_actual", n: "15A", label: "Actual Superheat", unit: "°F", kind: "number", required: true },
  { key: "superheat_target", n: "16A", label: "Target Superheat", unit: "°F", kind: "number", required: true },
  { key: "superheat_diff", n: "17A", label: "Difference (16A) − (15A)", unit: "°F", kind: "computed" },
];

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const r1 = (n: number) => Math.round(n * 10) / 10;
const sub = (a: unknown, b: unknown) => { const x = num(a), y = num(b); return x == null || y == null ? null : r1(x - y); };

/** The sheet's arithmetic. */
export function rebateCompute(d: RebateData) {
  const actual_split = sub(d.return_db, d.supply_db);
  const split_diff = actual_split == null ? null : sub(d.target_split, actual_split);
  const subcool_actual = sub(d.liquid_sat, d.liquid_temp);
  const subcool_diff = subcool_actual == null ? null : sub(d.subcool_target, subcool_actual);
  const superheat_diff = sub(d.superheat_target, d.superheat_actual);
  return { actual_split, split_diff, subcool_actual, subcool_diff, superheat_diff };
}

/** Every field that counts for this form (the metering choice picks TXV or piston lines). */
export function rebateFields(d: RebateData): RebateField[] {
  const met = d.metering === "piston" ? PISTON_FIELDS : d.metering === "txv" ? TXV_FIELDS : [];
  return [...HEADER_FIELDS, ...SIZING_FIELDS, ...AIRFLOW_FIELDS, ...met];
}

/** Required lines still blank (plain labels, for "12 of 22" and the Install Complete check). */
export function rebateMissing(d: RebateData): string[] {
  const out: string[] = [];
  for (const f of rebateFields(d)) {
    if (!f.required || f.kind === "computed") continue;
    const v = d[f.key];
    if (v === null || v === undefined || String(v).trim() === "") out.push(f.n ? `${f.n}. ${f.label}` : f.label);
  }
  if (d.metering !== "txv" && d.metering !== "piston") out.push("TXV or non-TXV system");
  if (!d.duct_method) out.push("Ductwork method");
  return out;
}
export function rebateCounts(d: RebateData) {
  const req = rebateFields(d).filter((f) => f.required && f.kind !== "computed").length + 2; // + metering + duct method
  const missing = rebateMissing(d).length;
  return { total: req, done: Math.max(0, req - missing) };
}

export interface RebateTestStatus { needed: boolean; auto: boolean; reason: string }
export function useRebateTestStatus(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["rebate_test_status", jobId ?? null],
    enabled: !!jobId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<RebateTestStatus | null> => {
      const { data, error } = await supabase.rpc("job_rebate_test_status", { p_job_id: jobId! });
      if (error) throw error;
      return (data ?? null) as RebateTestStatus | null;
    },
  });
}

export interface RebateTestRow {
  id: string; job_id: string; data: RebateData;
  completed_at: string | null; completed_by: string | null; completed_by_empno: string | null;
  updated_at: string; updated_by: string | null;
}
export function useRebateTest(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["rebate_test", jobId ?? null],
    enabled: !!jobId,
    queryFn: async (): Promise<RebateTestRow | null> => {
      const { data, error } = await supabase.from("pegasus_rebate_test").select("*").eq("job_id", jobId!).maybeSingle();
      if (error) throw error;
      return (data ?? null) as RebateTestRow | null;
    },
  });
}

/** The tech's ID from their last rebate form — typed once, remembered after. */
export function useLastTechId(empno: string | null | undefined) {
  return useQuery({
    queryKey: ["rebate_last_tech_id", empno ?? null],
    enabled: !!empno,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.from("pegasus_rebate_test").select("data, updated_at")
        .eq("completed_by_empno", empno!).order("updated_at", { ascending: false }).limit(5);
      for (const r of (data ?? []) as { data: RebateData }[]) {
        const id = String(r.data?.tech_id ?? "").trim();
        if (id) return id;
      }
      return null;
    },
  });
}

export function useSaveRebateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; data: RebateData; complete: boolean; empno: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const row: Record<string, unknown> = { job_id: v.jobId, data: v.data, updated_at: now, updated_by: email };
      if (v.complete) Object.assign(row, { completed_at: now, completed_by: email, completed_by_empno: v.empno });
      else Object.assign(row, { completed_at: null, completed_by: null });
      if (v.empno) row.completed_by_empno = v.empno;
      const { error } = await supabase.from("pegasus_rebate_test").upsert(row, { onConflict: "job_id" });
      if (error) throw error;
      // Share the readings with Log the Charge (one system on the job → one charge row).
      const c = rebateCompute(v.data);
      const { data: charges } = await supabase.from("pegasus_refrigerant_log").select("id").eq("job_id", v.jobId).eq("kind", "charge");
      const ids = ((charges ?? []) as { id: string }[]).map((x) => x.id);
      if (ids.length === 1 && (v.data.metering === "txv" || v.data.metering === "piston")) {
        const patch: Record<string, unknown> = { metering: v.data.metering, updated_at: now };
        if (v.data.metering === "txv") {
          if (c.subcool_actual != null) patch.subcool_actual = c.subcool_actual;
          if (num(v.data.subcool_target) != null) patch.subcool_target = num(v.data.subcool_target);
        } else {
          if (num(v.data.superheat_actual) != null) patch.superheat_actual = num(v.data.superheat_actual);
          if (num(v.data.superheat_target) != null) patch.superheat_target = num(v.data.superheat_target);
        }
        await supabase.from("pegasus_refrigerant_log").update(patch).eq("id", ids[0]);
      }
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["rebate_test", v.jobId] });
      void qc.invalidateQueries({ queryKey: ["job_refrigerant"] });
      void qc.invalidateQueries({ queryKey: ["rebate_last_tech_id"] });
    },
  });
}

/** The office forces the form on / off for a job (null = automatic). */
export function useSetRebateTestOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; value: "needed" | "not_needed" | null }) => {
      const { error } = await supabase.from("pegasus_job").update({ rebate_test: v.value, updated_at: new Date().toISOString() }).eq("id", v.jobId);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["rebate_test_status", v.jobId] });
      void qc.invalidateQueries({ queryKey: ["job"] });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** Today's outside temperature where the job is (Open-Meteo, free, no key) — a starting value, the crew checks it. */
export async function outsideTempNow(lat = 44.8041, lon = -93.1669): Promise<number | null> {
  try {
    const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m&temperature_unit=fahrenheit`);
    const j = await r.json();
    const t = Number(j?.current?.temperature_2m);
    return Number.isFinite(t) ? Math.round(t) : null;
  } catch {
    return null;
  }
}
